import React, { useState, useEffect } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'

const bgImage   = "https://hbylyyfhigscnrozlxqe.supabase.co/storage/v1/object/public/Background/Login_BG.png"  

export default function CaptchaGate({ onVerified }) {
    const [isVerifying, setIsVerifying] = useState(true)
    const [verificationFailed, setVerificationFailed] = useState(false)
    const [showV2, setShowV2] = useState(false)

    useEffect(() => {
        const timer = setTimeout(() => executeV3Captcha(), 1000)
        return () => clearTimeout(timer)
    }, [])

    const executeV3Captcha = async () => {
        setIsVerifying(true)
        setVerificationFailed(false)
        setShowV2(false)

        try {
            if (!window.grecaptcha) {
                throw new Error('reCAPTCHA not loaded')
            }

            const token = await new Promise((resolve, reject) => {
                window.grecaptcha.ready(async () => {
                    try {
                        const t = await window.grecaptcha.execute(
                            process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY,
                            { action: 'login_gate' }
                        )
                        resolve(t)
                    } catch (err) { reject(err) }
                })
            })

            // console.log('v3 token generated:', token)

            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
            const response = await fetch(`${API_URL}/api/verify-captcha`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, version: 'v3' })
            })

            const data = await response.json()
            // console.log('Verification response:', data)

            if (data.success && data.score >= 0.5) {
                sessionStorage.setItem('captchaVerified', 'true')
                sessionStorage.setItem('captchaTimestamp', Date.now().toString())
                onVerified()
            } else {
                console.log('Score too low, falling back to v2:', data.score)
                setShowV2(true)
                setIsVerifying(false)
            }

        } catch (error) {
            console.error('V3 Captcha error:', error)
            setShowV2(true)
            setIsVerifying(false)
        }
    }

    const handleV2Success = async (token) => {
        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
            const response = await fetch(`${API_URL}/api/verify-captcha`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, version: 'v2' })
            })
            const data = await response.json()

            if (data.success) {
                sessionStorage.setItem('captchaVerified', 'true')
                sessionStorage.setItem('captchaTimestamp', Date.now().toString())
                onVerified()
            } else {
                setVerificationFailed(true)
                setShowV2(false)
            }
        } catch (error) {
            console.error('V2 Captcha error:', error)
            setVerificationFailed(true)
            setShowV2(false)
        }
    }

    return (
        <div
            className="relative min-h-screen flex items-center justify-center"
            style={{
                backgroundImage: `url(${bgImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            }}
        >
            <div className='bg-gray-100 rounded-3xl shadow-2xl p-8 w-full max-w-md'>
                <div className='text-center mb-8'>
                    <div className='mx-auto w-16 h-16 bg-[#FF6F3C] rounded-full flex items-center justify-center mb-4'>
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
                            <g fill="none" stroke="white" strokeWidth="1.5">
                                <path d="M2 16c0-2.828 0-4.243.879-5.121C3.757 10 5.172 10 8 10h8c2.828 0 4.243 0 5.121.879C22 11.757 22 13.172 22 16s0 4.243-.879 5.121C20.243 22 18.828 22 16 22H8c-2.828 0-4.243 0-5.121-.879C2 20.243 2 18.828 2 16Z"/>
                                <path strokeLinecap="round" d="M6 10V8a6 6 0 1 1 12 0v2"/>
                            </g>
                        </svg>
                    </div>
                    <h1 className='text-3xl font-bold text-gray-800 mb-2'>Security Verification</h1>
                    <p className='text-gray-600'>
                        {isVerifying ? 'Verifying your session...' :
                         showV2 ? 'Please complete the verification below' :
                         verificationFailed ? 'Verification failed' : ''}
                    </p>
                </div>

                <div className='space-y-6'>
                    {isVerifying && (
                        <div className='flex justify-center'>
                            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#A0826D]'></div>
                        </div>
                    )}

                    {showV2 && !verificationFailed && (
                        <div className='flex justify-center'>
                            <ReCAPTCHA
                                sitekey={process.env.REACT_APP_RECAPTCHA_V2_SITE_KEY}
                                onChange={handleV2Success}
                            />
                        </div>
                    )}

                    {verificationFailed && (
                        <div className='text-center'>
                            <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-4'>
                                <p className='text-red-800 font-medium mb-2'>Access Denied</p>
                                <p className='text-red-600 text-sm'>
                                    We couldn't verify your session. This may be due to suspicious activity.
                                </p>
                            </div>
                            <button
                                onClick={executeV3Captcha}
                                className='w-full bg-[#FF6F3C] hover:bg-[#ff4c0b] text-white font-semibold py-3 rounded-lg transition duration-200'
                            >
                                Try Again
                            </button>
                        </div>
                    )}
                </div>

                <div className='mt-6 flex items-start gap-2 text-xs text-gray-500'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    <p>This verification helps protect our system from automated attacks and ensures the security of our staff portal.</p>
                </div>
            </div>
        </div>
    )
}